import { Grid, useMediaQuery } from "@mui/material";
import { Create, Delete } from "@mui/icons-material";
import { useAccount } from "wagmi";

import { Breadcrumbs, PageHeader } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { useDialogDelete, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { ListAction, ListActions, ListItem, StyledPagination } from "@framework/styled";
import { CollectionContractFeatures, ContractStatus, IContract } from "@framework/types";
import {
  BlacklistButton,
  BlockchainInfoPopover,
  CollectionContractDeployButton,
  CollectionUploadButton,
  ContractAllowanceButton,
  ContractSearchForm,
  EthListenerAddButton,
  EthListenerRemoveButton,
  GrantRoleButton,
  RenounceRoleButton,
  RevokeRoleButton,
  RoyaltyButton,
  ServiceType,
  SetBaseTokenURIButton,
  TransferButton,
  UnBlacklistButton,
  useGlobalDialogFormCallbacks,
  WithCheckPermissionsListWrapper,
} from "@framework/shared";
import { List } from "@dzambalaorg/ui-kit";

import { collectionsContractsValidationSchema } from "./form";
import { collectionsEditContractFilter, prepareCollectionsContractsInitialValues } from "./schemas";
import { AdminDialogKeys } from "../../../../../shared";

const COLLECTION_CONTRACTS_URL = "/collection/contracts";

export const CollectionContract = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { data, isLoading, isError, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } =
    useFetchQuery<IPaginationResult<IContract>>(COLLECTION_CONTRACTS_URL, { enableInfiniteScroll: isMd }, void 0, {
      contractStatus: [ContractStatus.ACTIVE, ContractStatus.NEW, ContractStatus.INACTIVE],
      contractFeatures: [],
    });

  const { openDialog } = useGlobalDialogFormCallbacks<IContract, IContract>();
  const { handleDelete } = useDialogDelete<IContract>();

  const { address: account = "" } = useAccount();

  const collectionsContractEdit = (contract: IContract) => () => {
    openDialog(AdminDialogKeys.COLLECTIONS_CONTRACT_EDIT, contract, {
      refetch,
      initialValues: contract,
      message: "dialogs.edit",
      testId: "Erc721CollectionDialogForm",
      url: `${COLLECTION_CONTRACTS_URL}/${contract.id}`,
      validationSchema: collectionsContractsValidationSchema,
      filter: collectionsEditContractFilter,
      prepareInitialValues: prepareCollectionsContractsInitialValues,
      action: (
        <BlockchainInfoPopover
          name={contract.name}
          symbol={contract.symbol}
          owner={contract.parameters.owner}
          batchSize={contract.parameters.batchSize}
          baseTokenURI={contract.baseTokenURI}
          royalty={`${contract.royalty / 100}%`}
          chainId={contract.chainId}
          contractFeatures={contract.contractFeatures}
        />
      ),
    });
  };

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "collection", "collection.contract"]} />

      <PageHeader message="pages.collection.contracts">
        <CollectionContractDeployButton />
      </PageHeader>

      <ContractSearchForm
        initialSearchValues={{
          contractStatus: [ContractStatus.ACTIVE, ContractStatus.NEW, ContractStatus.INACTIVE],
          contractFeatures: [],
        }}
        service={ServiceType.ADMIN}
        contractFeaturesOptions={CollectionContractFeatures}
      />

      <WithCheckPermissionsListWrapper
        isLoading={!data && isLoading}
        isError={!data && isError}
        isEmpty={allRows.length === 0}
        count={allRows.length}
      >
        {allRows.map(contract => (
          <ListItem key={contract.id} account={account} contract={contract}>
            <List.ItemText sx={{ width: 0.6 }}>{contract.title}</List.ItemText>
            <List.ItemText>{contract.parameters.batchSize}</List.ItemText>
            <ListActions dataTestId="CollectionActionsMenuButton">
              <ListAction
                onClick={collectionsContractEdit(contract)}
                message="form.buttons.edit"
                dataTestId="ContractEditButton"
                icon={Create}
              />
              <ListAction
                onClick={handleDelete(contract)}
                message="form.buttons.delete"
                dataTestId="ContractDeleteButton"
                icon={Delete}
                disabled={contract.contractStatus === ContractStatus.INACTIVE}
              />
              <GrantRoleButton contract={contract} />
              <RevokeRoleButton contract={contract} />
              <RenounceRoleButton contract={contract} />
              <ContractAllowanceButton contract={contract} />
              <RoyaltyButton contract={contract} />
              <SetBaseTokenURIButton contract={contract} />
              <BlacklistButton contract={contract} />
              <UnBlacklistButton contract={contract} />
              <TransferButton contract={contract} />
              <CollectionUploadButton contract={contract} onRefreshPage={refetch} />
              <EthListenerAddButton contract={contract} />
              <EthListenerRemoveButton contract={contract} />
            </ListActions>
          </ListItem>
        ))}
      </WithCheckPermissionsListWrapper>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={COLLECTION_CONTRACTS_URL} refetch={refetch} />
    </Grid>
  );
};
